import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { ExternalLink, AlertCircle } from 'lucide-react';

const TermsPage = () => {
  const { language, t } = useLanguage();

  return (
    <div className="min-h-screen bg-gray-50" data-testid="terms-page">
      <SEO
        title={t('Algemene Voorwaarden - U&Fashion', 'Terms of Service - U&Fashion')}
        description={t(
          'Algemene voorwaarden voor het gebruik van U&Fashion.eu',
          'Terms of service for using U&Fashion.eu'
        )}
      />

      <div className="bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl font-bold mb-4">
            {t('Algemene Voorwaarden', 'Terms of Service')}
          </h1>
          <p className="text-xl text-white/90">
            {t(
              'Voorwaarden voor het gebruik van onze website',
              'Terms and conditions for using our website'
            )}
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-xl shadow-md p-8 space-y-8">
          {/* Dutch Version */}
          {language === 'nl' ? (
            <>
              {/* Critical Notice */}
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-6 rounded-lg text-white">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-8 h-8 flex-shrink-0 mt-1" />
                  <div>
                    <h2 className="text-2xl font-bold mb-3">
                      ⚠️ BELANGRIJK: Aankopen via Onze Partners
                    </h2>
                    <p className="text-lg leading-relaxed mb-2">
                      <strong>U&Fashion.eu is GEEN webshop.</strong> Wij verkopen zelf geen producten en verwerken geen bestellingen.
                    </p>
                    <p className="text-lg leading-relaxed">
                      Wanneer u via onze website een product aankoopt, gelden <strong>UITSLUITEND de algemene 
                      voorwaarden, het privacybeleid en het retourbeleid van de partner-retailer waar u bestelt</strong>.
                    </p>
                  </div>
                </div>
              </div>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  1. Over U&Fashion.eu
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  U&Fashion.eu is een affiliate marketing platform dat gebruikers helpt bij het ontdekken van 
                  fashion producten van verschillende online retailers. Wij fungeren als <strong>doorverwijzer</strong> 
                  en presenteren productinformatie met links naar de websites van onze partners.
                </p>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-gray-700">
                    <strong>Onze rol:</strong> Wij bieden een overzicht van producten en faciliteren de doorverwijzing 
                    naar de websites van retailers. De daadwerkelijke verkoop gebeurt bij de partner-retailer.
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  2. Welke Voorwaarden Zijn van Toepassing?
                </h2>
                <div className="space-y-4">
                  <div className="border-l-4 border-green-500 pl-4 py-2 bg-green-50">
                    <p className="text-gray-900 font-semibold mb-2">
                      ✓ Voor het gebruik van U&Fashion.eu (browsen, zoeken)
                    </p>
                    <p className="text-gray-700">
                      Deze algemene voorwaarden zijn van toepassing op uw gebruik van onze website voor 
                      het bekijken van productinformatie en het klikken op links.
                    </p>
                  </div>

                  <div className="border-l-4 border-purple-500 pl-4 py-2 bg-purple-50">
                    <p className="text-gray-900 font-semibold mb-2">
                      ✓ Voor aankopen en bestellingen
                    </p>
                    <p className="text-gray-700 mb-2">
                      Zodra u op een productlink klikt en wordt doorverwezen naar een partner-website, 
                      zijn <strong>UITSLUITEND de voorwaarden van die partner van toepassing</strong>, waaronder:
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                      <li>Hun algemene voorwaarden</li>
                      <li>Hun privacybeleid</li>
                      <li>Hun retourbeleid</li>
                      <li>Hun betalingsvoorwaarden</li>
                      <li>Hun leveringsvoorwaarden</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="bg-red-50 p-6 rounded-lg border-2 border-red-200">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  3. Geen Contractuele Relatie bij Aankopen
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Door het plaatsen van een bestelling via een van onze partnerlinks:
                </p>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <span className="text-red-600 mr-3 text-xl font-bold">•</span>
                    <p className="text-gray-700">
                      Komt er <strong>GEEN koopovereenkomst</strong> tot stand tussen u en U&Fashion.eu
                    </p>
                  </div>
                  <div className="flex items-start">
                    <span className="text-red-600 mr-3 text-xl font-bold">•</span>
                    <p className="text-gray-700">
                      De koopovereenkomst wordt <strong>UITSLUITEND</strong> gesloten tussen u en de partner-retailer
                    </p>
                  </div>
                  <div className="flex items-start">
                    <span className="text-red-600 mr-3 text-xl font-bold">•</span>
                    <p className="text-gray-700">
                      U&Fashion.eu is <strong>GEEN partij</strong> bij deze overeenkomst
                    </p>
                  </div>
                  <div className="flex items-start">
                    <span className="text-red-600 mr-3 text-xl font-bold">•</span>
                    <p className="text-gray-700">
                      Alle rechten en plichten voortvloeiend uit de aankoop bestaan tussen u en de partner-retailer
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  4. Gebruik van Onze Website
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Door gebruik te maken van U&Fashion.eu stemt u ermee in om:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>De website alleen te gebruiken voor persoonlijke, niet-commerciële doeleinden</li>
                  <li>Geen geautomatiseerde systemen te gebruiken om onze website te scrapen of data te verzamelen</li>
                  <li>Geen misbruik te maken van onze affiliate links</li>
                  <li>De algemene voorwaarden van onze partners te respecteren</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  5. Productinformatie en Prijzen
                </h2>
                <div className="bg-yellow-50 p-4 rounded-lg mb-4">
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Let op:</strong> Productinformatie, prijzen, beschikbaarheid en afbeeldingen worden 
                    verzameld van onze partners. Hoewel wij ons best doen om actuele informatie te tonen, kunnen 
                    er verschillen zijn. De informatie op de website van de partner is altijd leidend.
                  </p>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  U&Fashion.eu aanvaardt geen aansprakelijkheid voor onjuistheden in productinformatie, 
                  prijzen of beschikbaarheid.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                  <ExternalLink className="w-6 h-6 mr-2 text-purple-500" />
                  6. Doorverwijzing naar Partner Websites
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Wanneer u op een productlink klikt:
                </p>
                <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                  <div className="flex items-start">
                    <span className="text-purple-500 mr-3">1.</span>
                    <p className="text-gray-700">
                      U verlaat U&Fashion.eu en wordt doorverwezen naar de website van een partner-retailer
                    </p>
                  </div>
                  <div className="flex items-start">
                    <span className="text-purple-500 mr-3">2.</span>
                    <p className="text-gray-700">
                      Vanaf dat moment zijn de voorwaarden en het privacybeleid van die partner van toepassing
                    </p>
                  </div>
                  <div className="flex items-start">
                    <span className="text-purple-500 mr-3">3.</span>
                    <p className="text-gray-700">
                      U&Fashion.eu heeft geen controle over deze externe websites en is niet verantwoordelijk 
                      voor hun inhoud, beleid of praktijken
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  7. Aansprakelijkheid
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  U&Fashion.eu is niet aansprakelijk voor:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Schade voortvloeiend uit aankopen bij onze partners</li>
                  <li>Geschillen tussen u en een partner-retailer</li>
                  <li>Verlies van data of inkomsten</li>
                  <li>Indirecte of gevolgschade</li>
                  <li>Onderbreking van onze dienstverlening</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  8. Intellectueel Eigendom
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  Alle inhoud op U&Fashion.eu, inclusief maar niet beperkt tot teksten, logo's, afbeeldingen 
                  en software, is eigendom van U&Fashion.eu of gelicentieerd aan ons. Productafbeeldingen en 
                  merklogo's blijven eigendom van de respectievelijke merken en retailers.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  9. Wijzigingen
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  Wij behouden ons het recht voor om deze algemene voorwaarden te allen tijde te wijzigen. 
                  Wijzigingen worden op deze pagina gepubliceerd met een nieuwe datum van laatste update.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  10. Contact
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Voor vragen over deze algemene voorwaarden kunt u contact met ons opnemen via de contactpagina.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>Voor vragen over bestellingen, leveringen of retourzendingen:</strong> Neem rechtstreeks 
                  contact op met de retailer waar u besteld heeft.
                </p>
              </section>

              <section className="bg-gray-100 p-6 rounded-lg">
                <p className="text-sm text-gray-600 leading-relaxed">
                  <strong>Laatste update:</strong> {new Date().toLocaleDateString('nl-BE')}<br/>
                  <strong>Toepasselijk recht:</strong> Op deze algemene voorwaarden is Belgisch recht van toepassing.
                </p>
              </section>

              <div className="pt-4 border-t-2 border-gray-200">
                <p className="text-gray-600 text-sm">
                  Zie ook onze <Link to="/disclaimer" className="text-purple-600 hover:text-purple-700 font-semibold">Affiliate Disclaimer</Link> voor 
                  meer informatie over onze rol als affiliate partner.
                </p>
              </div>
            </>
          ) : (
            /* English Version */
            <>
              {/* Critical Notice */}
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-6 rounded-lg text-white">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-8 h-8 flex-shrink-0 mt-1" />
                  <div>
                    <h2 className="text-2xl font-bold mb-3">
                      ⚠️ IMPORTANT: Purchases via Our Partners
                    </h2>
                    <p className="text-lg leading-relaxed mb-2">
                      <strong>U&Fashion.eu is NOT a webshop.</strong> We do not sell products ourselves and do not process orders.
                    </p>
                    <p className="text-lg leading-relaxed">
                      When you purchase a product through our website, <strong>ONLY the terms and conditions, 
                      privacy policy, and return policy of the partner retailer where you order apply</strong>.
                    </p>
                  </div>
                </div>
              </div>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  1. About U&Fashion.eu
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  U&Fashion.eu is an affiliate marketing platform that helps users discover fashion products from 
                  various online retailers. We act as a <strong>referrer</strong> and present product information 
                  with links to our partners' websites.
                </p>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-gray-700">
                    <strong>Our role:</strong> We provide an overview of products and facilitate referrals to 
                    retailers' websites. The actual sale takes place at the partner retailer.
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  2. Which Terms Apply?
                </h2>
                <div className="space-y-4">
                  <div className="border-l-4 border-green-500 pl-4 py-2 bg-green-50">
                    <p className="text-gray-900 font-semibold mb-2">
                      ✓ For using U&Fashion.eu (browsing, searching)
                    </p>
                    <p className="text-gray-700">
                      These terms of service apply to your use of our website for viewing product information 
                      and clicking on links.
                    </p>
                  </div>

                  <div className="border-l-4 border-purple-500 pl-4 py-2 bg-purple-50">
                    <p className="text-gray-900 font-semibold mb-2">
                      ✓ For purchases and orders
                    </p>
                    <p className="text-gray-700 mb-2">
                      Once you click on a product link and are redirected to a partner website, 
                      <strong> ONLY that partner's terms apply</strong>, including:
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                      <li>Their terms and conditions</li>
                      <li>Their privacy policy</li>
                      <li>Their return policy</li>
                      <li>Their payment terms</li>
                      <li>Their delivery terms</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="bg-red-50 p-6 rounded-lg border-2 border-red-200">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  3. No Contractual Relationship for Purchases
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  By placing an order through one of our partner links:
                </p>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <span className="text-red-600 mr-3 text-xl font-bold">•</span>
                    <p className="text-gray-700">
                      <strong>NO purchase agreement</strong> is formed between you and U&Fashion.eu
                    </p>
                  </div>
                  <div className="flex items-start">
                    <span className="text-red-600 mr-3 text-xl font-bold">•</span>
                    <p className="text-gray-700">
                      The purchase agreement is formed <strong>EXCLUSIVELY</strong> between you and the partner retailer
                    </p>
                  </div>
                  <div className="flex items-start">
                    <span className="text-red-600 mr-3 text-xl font-bold">•</span>
                    <p className="text-gray-700">
                      U&Fashion.eu is <strong>NOT a party</strong> to this agreement
                    </p>
                  </div>
                  <div className="flex items-start">
                    <span className="text-red-600 mr-3 text-xl font-bold">•</span>
                    <p className="text-gray-700">
                      All rights and obligations arising from the purchase exist between you and the partner retailer
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  4. Use of Our Website
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  By using U&Fashion.eu, you agree to:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Use the website only for personal, non-commercial purposes</li>
                  <li>Not use automated systems to scrape or collect data from our website</li>
                  <li>Not abuse our affiliate links</li>
                  <li>Respect the terms and conditions of our partners</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  5. Product Information and Prices
                </h2>
                <div className="bg-yellow-50 p-4 rounded-lg mb-4">
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Please note:</strong> Product information, prices, availability, and images are 
                    collected from our partners. While we strive to display current information, differences 
                    may occur. The information on the partner's website is always leading.
                  </p>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  U&Fashion.eu accepts no liability for inaccuracies in product information, prices, or availability.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                  <ExternalLink className="w-6 h-6 mr-2 text-purple-500" />
                  6. Referral to Partner Websites
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  When you click on a product link:
                </p>
                <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                  <div className="flex items-start">
                    <span className="text-purple-500 mr-3">1.</span>
                    <p className="text-gray-700">
                      You leave U&Fashion.eu and are redirected to a partner retailer's website
                    </p>
                  </div>
                  <div className="flex items-start">
                    <span className="text-purple-500 mr-3">2.</span>
                    <p className="text-gray-700">
                      From that moment, that partner's terms and privacy policy apply
                    </p>
                  </div>
                  <div className="flex items-start">
                    <span className="text-purple-500 mr-3">3.</span>
                    <p className="text-gray-700">
                      U&Fashion.eu has no control over these external websites and is not responsible for 
                      their content, policies, or practices
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  7. Liability
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  U&Fashion.eu is not liable for:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Damage resulting from purchases at our partners</li>
                  <li>Disputes between you and a partner retailer</li>
                  <li>Loss of data or revenue</li>
                  <li>Indirect or consequential damages</li>
                  <li>Interruption of our services</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  8. Intellectual Property
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  All content on U&Fashion.eu, including but not limited to text, logos, images, and software, 
                  is owned by U&Fashion.eu or licensed to us. Product images and brand logos remain the property 
                  of their respective brands and retailers.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  9. Changes
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  We reserve the right to modify these terms of service at any time. Changes will be published 
                  on this page with a new last updated date.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  10. Contact
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  For questions about these terms of service, you can contact us via the contact page.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>For questions about orders, deliveries, or returns:</strong> Please contact the retailer 
                  where you placed your order directly.
                </p>
              </section>

              <section className="bg-gray-100 p-6 rounded-lg">
                <p className="text-sm text-gray-600 leading-relaxed">
                  <strong>Last updated:</strong> {new Date().toLocaleDateString('en-US')}<br/>
                  <strong>Applicable law:</strong> These terms of service are governed by Belgian law.
                </p>
              </section>

              <div className="pt-4 border-t-2 border-gray-200">
                <p className="text-gray-600 text-sm">
                  Also see our <Link to="/disclaimer" className="text-purple-600 hover:text-purple-700 font-semibold">Affiliate Disclaimer</Link> for 
                  more information about our role as an affiliate partner.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
